"use client";

import { type UniversityType, type CountryType } from "@/src/lib/universities";
import PaginatedContent from "@/src/components/ui/PaginatedContent";
import UniversityCard from "@/src/components/universities/UniversityCard";
import CountriesTabs from "@/src/components/universities/CountriesTabs";
import { useMemo, useEffect } from "react";
import { usePathname, useRouter } from "@/src/navigation";
import { useSearchParams } from "next/navigation";

type TabedUniversitiesProps = {
  universities: UniversityType[];
  countries: CountryType[];
};

const TabedUniversities = ({
  universities,
  countries,
}: TabedUniversitiesProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const countryIds = useMemo(
    () => countries.map((country) => country.id),
    [countries],
  );
  const urlCountryValue = searchParams.get("country");

  function getValidCountry(str: string | null) {
    const number = Number(str);
    if (!number || isNaN(number)) return 0;
    if (!countryIds.includes(number)) return 0;
    return number;
  }

  const activeId = getValidCountry(urlCountryValue);

  useEffect(() => {
    if (activeId !== Number(urlCountryValue)) {
      const params = new URLSearchParams(searchParams.toString());
      params.set("country", activeId.toString());
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  }, [activeId, urlCountryValue, pathname, router, searchParams]);

  function handleClick(id: number) {
    const params = new URLSearchParams(searchParams.toString());
    params.set("country", id.toString());
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  }

  const selectedUniversities = useMemo(() => {
    return activeId === 0
      ? universities
      : universities.filter((university) => university.country === activeId);
  }, [activeId, universities]);

  return (
    <>
      <CountriesTabs
        countries={countries}
        activeId={activeId}
        handleClick={handleClick}
      />
      <PaginatedContent
        itemsPerPage={9}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8"
      >
        {selectedUniversities.map((university) => (
          <UniversityCard key={university.id} {...university} />
        ))}
      </PaginatedContent>
    </>
  );
};

export default TabedUniversities;
